import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { TrendingUp, Globe, Building, Award, Users, Package, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const DXNPhilosophy = () => (
  <div className="space-y-8">
    {/* One Dragon Concept */}
    <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white">
      <h2 className="text-3xl font-bold mb-6 text-center">"One Dragon" - Complete Vertical Integration</h2>
      <p className="text-emerald-100 text-center max-w-3xl mx-auto mb-8">
        DXN's unique philosophy of controlling every aspect from farm to consumer, ensuring uncompromising quality and value
      </p>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 max-w-5xl mx-auto">
        {[
          { step: '1', title: 'Organic Cultivation', desc: '40 acres farms' },
          { step: '2', title: 'GMP Manufacturing', desc: '10 facilities' },
          { step: '3', title: 'Quality Control', desc: 'ISO certified' },
          { step: '4', title: 'Direct Distribution', desc: 'No middlemen' },
          { step: '5', title: 'Consumer Value', desc: 'Best pricing' }
        ].map((item, idx) => (
          <div key={idx} className="text-center">
            <div className="w-16 h-16 rounded-full bg-white text-emerald-700 flex items-center justify-center mx-auto mb-3 font-bold text-2xl">
              {item.step}
            </div>
            <h4 className="font-bold text-white mb-1">{item.title}</h4>
            <p className="text-sm text-emerald-200">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Benefits of One Dragon */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white rounded-xl p-6 shadow-md border-2 border-emerald-200">
        <h4 className="font-bold text-gray-900 mb-3">Quality Control at Every Stage</h4>
        <p className="text-sm text-gray-600">From seed to product, DXN maintains complete oversight ensuring the highest standards without third-party contamination.</p>
      </div>
      <div className="bg-white rounded-xl p-6 shadow-md border-2 border-blue-200">
        <h4 className="font-bold text-gray-900 mb-3">Cost Efficiency</h4>
        <p className="text-sm text-gray-600">Eliminating middlemen reduces costs, allowing better value for distributors and consumers while maintaining premium quality.</p>
      </div>
      <div className="bg-white rounded-xl p-6 shadow-md border-2 border-amber-200">
        <h4 className="font-bold text-gray-900 mb-3">Farm-to-Consumer Transparency</h4>
        <p className="text-sm text-gray-600">Complete traceability from organic cultivation through manufacturing to final product delivery ensures trust.</p>
      </div>
    </div>

    {/* One World, One Market, One Mind */}
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">"One World, One Market, One Mind"</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Global Unity</h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Single global membership - access to 180+ markets</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Unified compensation plan worldwide</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>International business building opportunities</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Cultural inclusivity across nations</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Shared Vision</h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start"><span className="text-indigo-600 mr-2">•</span>Common values of integrity and excellence</li>
            <li className="flex items-start"><span className="text-indigo-600 mr-2">•</span>Collective prosperity mindset</li>
            <li className="flex items-start"><span className="text-indigo-600 mr-2">•</span>Supporting each other's success</li>
            <li className="flex items-start"><span className="text-indigo-600 mr-2">•</span>Building a global family</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Network Marketing Philosophy */}
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Empowerment Through Network Marketing</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
          <h4 className="font-bold text-gray-900 mb-4">Empowerment Focus</h4>
          <ul className="space-y-3 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="text-emerald-600 font-bold mr-2">•</span>
              <div><strong>Low Barrier to Entry:</strong> Minimal investment required to start</div>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 font-bold mr-2">•</span>
              <div><strong>Scalable Income:</strong> Unlimited earning potential based on effort</div>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 font-bold mr-2">•</span>
              <div><strong>Personal Development:</strong> Training and skill enhancement</div>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 font-bold mr-2">•</span>
              <div><strong>Global Opportunities:</strong> Build business in 180+ countries</div>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 font-bold mr-2">•</span>
              <div><strong>Community Support:</strong> Teamwork and collective growth</div>
            </li>
          </ul>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
          <h4 className="font-bold text-gray-900 mb-4">Recognition & Incentives</h4>
          <ul className="space-y-3 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="text-amber-600 font-bold mr-2">•</span>
              <div><strong>Crown Ambassador:</strong> Highest achievement status</div>
            </li>
            <li className="flex items-start">
              <span className="text-amber-600 font-bold mr-2">•</span>
              <div><strong>International Travel Seminar Incentives (ITSI):</strong> Global trips</div>
            </li>
            <li className="flex items-start">
              <span className="text-amber-600 font-bold mr-2">•</span>
              <div><strong>Mobile Phone Cash Incentives:</strong> Technology support</div>
            </li>
            <li className="flex items-start">
              <span className="text-amber-600 font-bold mr-2">•</span>
              <div><strong>Group Bonus Structures:</strong> Team-based rewards</div>
            </li>
            <li className="flex items-start">
              <span className="text-amber-600 font-bold mr-2">•</span>
              <div><strong>Global Conventions:</strong> 30th Anniversary - 10,000 members</div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    {/* Ethical Approach */}
    <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Ethical Business Practices</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <span className="text-green-600 text-xl mr-2">✓</span> Honest Practices
          </h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• Product quality over hype</li>
            <li>• No false promises</li>
            <li>• Transparent business model</li>
            <li>• Focus on genuine benefits</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <span className="text-blue-600 text-xl mr-2">✓</span> Sustainability
          </h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• Long-term vision</li>
            <li>• Compliance with regulations</li>
            <li>• Member of DSAM</li>
            <li>• Ethical growth strategy</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <span className="text-purple-600 text-xl mr-2">✓</span> Social Impact
          </h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• Creating global employment</li>
            <li>• Women's empowerment</li>
            <li>• Rural development</li>
            <li>• Philanthropy integration</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Business as Vehicle for Good */}
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Business as Vehicle for Good</h3>
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h4 className="font-bold text-gray-900 mb-2">Peace Through Prosperity</h4>
          <p className="text-sm text-gray-600">Economic independence reduces conflict. When people have sustainable income and opportunity, peace naturally follows.</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h4 className="font-bold text-gray-900 mb-2">Health Reduces Suffering</h4>
          <p className="text-sm text-gray-600">Providing access to wellness products and knowledge helps communities overcome health challenges and improve quality of life.</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h4 className="font-bold text-gray-900 mb-2">Education Empowers Communities</h4>
          <p className="text-sm text-gray-600">Training and skill development through DXN creates lasting impact beyond financial success.</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h4 className="font-bold text-gray-900 mb-2">Global Family Creation</h4>
          <p className="text-sm text-gray-600">The network creates bonds across cultures and nations, fostering understanding and cooperation.</p>
        </div>
      </div>
    </div>
  </div>
);

const DXNStory = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-emerald-50 to-gray-50 rounded-2xl p-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">The DXN Story: From Vision to Global Reality</h2>
      <div className="prose max-w-none">
        <p className="text-gray-700 leading-relaxed mb-4">
          Founded in 1993 in Alor Setar, Kedah, Malaysia, DXN Holdings Bhd emerged from Dr. Lim Siow Jin's profound vision: to make Ganoderma Lucidum (Lingzhi/Reishi mushroom) a household wellness product worldwide. This vision was born from his deep research into Traditional Chinese Medicine and the remarkable healing properties of this "King of Herbs."
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          What started as a humble operation has grown into a global wellness empire, operating in 180+ countries with 15-20 million distributors. DXN's success story is built on the foundation of the "One Dragon" philosophy - complete vertical integration from organic cultivation to global distribution.
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
            <Globe className="text-emerald-600" size={24} />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Global Presence</h3>
        </div>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-emerald-600"></div>
            <span>180+ countries worldwide</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-emerald-600"></div>
            <span>15-20 million distributors</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-emerald-600"></div>
            <span>RM1.6 billion revenue (FY2023)</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-emerald-600"></div>
            <span>Major markets across all continents</span>
          </li>
        </ul>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <Award className="text-amber-600" size={24} />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Certifications</h3>
        </div>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-amber-600"></div>
            <span>GMP Certified (First in Malaysia, 1999)</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-amber-600"></div>
            <span>ISO 9001:2015 Quality Management</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-amber-600"></div>
            <span>ISO 14001 Environmental Management</span>
          </li>
          <li className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-amber-600"></div>
            <span>TGA Approved (Australia)</span>
          </li>
        </ul>
      </div>
    </div>

    {/* Timeline */}
    <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
      <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">DXN Journey Timeline</h3>
      <div className="space-y-6">
        <div className="flex items-start space-x-4">
          <div className="w-24 flex-shrink-0 text-right">
            <span className="text-emerald-700 font-bold text-lg">1993</span>
          </div>
          <div className="w-4 h-4 rounded-full bg-emerald-600 mt-1.5 flex-shrink-0"></div>
          <div className="flex-1">
            <h4 className="font-bold text-gray-900 mb-1">DXN Founded</h4>
            <p className="text-gray-600 text-sm">Established in Alor Setar, Kedah, Malaysia</p>
          </div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="w-24 flex-shrink-0 text-right">
            <span className="text-emerald-700 font-bold text-lg">2003</span>
          </div>
          <div className="w-4 h-4 rounded-full bg-emerald-600 mt-1.5 flex-shrink-0"></div>
          <div className="flex-1">
            <h4 className="font-bold text-gray-900 mb-1">IPO Launch</h4>
            <p className="text-gray-600 text-sm">First listed on Bursa Malaysia Main Board</p>
          </div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="w-24 flex-shrink-0 text-right">
            <span className="text-emerald-700 font-bold text-lg">2011</span>
          </div>
          <div className="w-4 h-4 rounded-full bg-emerald-600 mt-1.5 flex-shrink-0"></div>
          <div className="flex-1">
            <h4 className="font-bold text-gray-900 mb-1">Privatization</h4>
            <p className="text-gray-600 text-sm">Company privatized by founder</p>
          </div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="w-24 flex-shrink-0 text-right">
            <span className="text-emerald-700 font-bold text-lg">2023</span>
          </div>
          <div className="w-4 h-4 rounded-full bg-emerald-600 mt-1.5 flex-shrink-0"></div>
          <div className="flex-1">
            <h4 className="font-bold text-gray-900 mb-1">Re-listing & 30th Anniversary</h4>
            <p className="text-gray-600 text-sm">Re-listed on Bursa Malaysia, celebrated 30 years with 10,000 members at DXN Cyberville</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const DXN = () => {
  const { section } = useParams();

  const sections = [
    { id: 'story', name: 'The DXN Story', component: DXNStory },
    { id: 'philosophy', name: 'Business Philosophy', component: DXNPhilosophy },
    { id: 'products', name: 'Products & Innovation', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Products - Coming Soon</h2></div> },
    { id: 'global', name: 'Global Presence', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Global Presence - Coming Soon</h2></div> },
    { id: 'cyberville', name: 'DXN Cyberville', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">DXN Cyberville - Coming Soon</h2></div> },
    { id: 'milestones', name: 'Corporate Milestones', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Milestones - Coming Soon</h2></div> },
    { id: 'success', name: 'Success Stories', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Success Stories - Coming Soon</h2></div> }
  ];

  const currentSection = section || 'story';
  const CurrentComponent = sections.find(s => s.id === currentSection)?.component || DXNStory;

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="DXN - My Business Legacy"
        description="Discover DXN Holdings Bhd - the global wellness empire founded by Dr. Lim Siow Jin. Operating in 180+ countries with 15-20 million distributors. Learn about the 'One Dragon' philosophy."
        keywords="DXN Holdings, Ganoderma business, network marketing, One Dragon philosophy, wellness company, Dr. Lim Siow Jin business, RM1.6B revenue"
        
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">DXN - My Business Legacy</h1>
              <p className="text-xl text-emerald-100">30+ years of building a global wellness empire with integrity</p>
            </div>
            <SocialShare 
              title="DXN - Dr. Lim Siow Jin's Business Legacy"
              description="30+ years of building a global wellness empire in 180+ countries with the One Dragon philosophy."
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {sections.map((sec) => (
              <Link
                key={sec.id}
                to={`/dxn/${sec.id}`}
                className={`px-6 py-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                  currentSection === sec.id
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-600 hover:text-emerald-700 hover:border-gray-300'
                }`}
              >
                {sec.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <CurrentComponent />
      </div>
    </div>
  );
};

export default DXN;